import { inject, Injectable } from '@angular/core';

import { ConversationalChatPersistenceService } from './conversational-chat-persistence.service';

@Injectable({
  providedIn: 'root',
})
export class ConversationalChatSessionService {
  readonly #conversationalChatPersistenceService = inject(
    ConversationalChatPersistenceService
  );

  readonly #webchatSessionTimeoutInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  getWebchatState(): {
    sessionId: string;
    shouldOpenLastConversation: boolean;
  } {
    let sessionId = this.#conversationalChatPersistenceService.sessionId;
    let shouldOpenLastConversation = false;

    if (sessionId && this.#isValidSession(sessionId)) {
      shouldOpenLastConversation = true;
    } else {
      sessionId = this.#createAndPersistNewSessionId();
    }

    return {
      sessionId,
      shouldOpenLastConversation,
    };
  }

  #isValidSession(sessionId: string): boolean {
    const hasLastConversationSessionExpired =
      this.#hasLastConversationSessionExpired(sessionId);

    // if we can't find a conversation (undefined), we assume that a new session has just started
    return hasLastConversationSessionExpired === undefined
      ? true
      : !hasLastConversationSessionExpired;
  }

  #hasLastConversationSessionExpired(sessionId: string) {
    const lastConversationKey =
      this.#conversationalChatPersistenceService.getLastConversationKey(
        sessionId
      );

    if (!lastConversationKey) {
      return undefined;
    }
    const lastConversation =
      this.#conversationalChatPersistenceService.getLastConversation(
        lastConversationKey
      );

    if (!lastConversation) {
      return undefined;
    }

    const timestamp = lastConversation?.messages?.[0]?.timestamp;
    if (!timestamp) {
      return undefined;
    }

    return Date.now() - timestamp > this.#webchatSessionTimeoutInMs;
  }

  #createAndPersistNewSessionId() {
    const newSessionId = this.#generateSessionId();
    this.#conversationalChatPersistenceService.setSessionId(newSessionId);
    return newSessionId;
  }

  #generateSessionId() {
    return `session-${crypto.randomUUID()}`;
  }
}
